/**
 * Library — every creative asset for this client:
 *   • uploaded (name/description/author/licence)
 *   • AI-created (as above + AI platform, model, brief)
 *   • URL-only videos hosted on YouTube/Vimeo (thumbnail + metadata)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  LibraryBig, Trash2, Bot, Upload, FileAudio, FileVideo, FileText, Scale,
  ExternalLink, Youtube, Type as TypeIcon, Play, Plus, X, Loader2, Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";

const fileUrl = (a) => `${API}/creative/assets/${a.asset_id}/file`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU",
  { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtSize = (b) => {
  const n = Number(b) || 0;
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDuration = (s) => {
  const n = Number(s) || 0;
  if (!n) return "";
  const m = Math.floor(n / 60);
  const sec = Math.round(n % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

function Preview({ a }) {
  const mime = a.mime || "";

  // URL-only (YouTube / Vimeo)
  if (a.origin === "url") {
    return (
      <a href={a.url} target="_blank" rel="noreferrer"
        className="block relative group">
        {a.thumbnail_url ? (
          <img src={a.thumbnail_url} alt={a.name}
            className="w-full h-44 object-cover" />
        ) : (
          <div className="h-44 grid place-items-center bg-zinc-100">
            <Youtube className="h-8 w-8 text-zinc-400" />
          </div>
        )}
        <div className="absolute inset-0 grid place-items-center bg-black/20 group-hover:bg-black/40 transition-colors">
          <span className="rounded-full bg-white/95 p-2 shadow-md">
            <Play className="h-6 w-6 text-zinc-800" />
          </span>
        </div>
        {a.duration_seconds && (
          <span className="absolute bottom-1 right-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded font-mono">
            {fmtDuration(a.duration_seconds)}
          </span>
        )}
      </a>
    );
  }

  // Seedance-hosted video URL (saved from AI generation)
  if (a.kind === "video" && a.video_url && !a.disk_path) {
    return (
      <video src={a.video_url} controls
        className="w-full h-44 object-cover bg-black" />
    );
  }

  if (mime.startsWith("image/")) {
    return (
      <a href={fileUrl(a)} target="_blank" rel="noreferrer">
        <img src={fileUrl(a)} alt={a.name} className="w-full h-44 object-cover" />
      </a>
    );
  }
  if (mime.startsWith("audio/")) {
    return (
      <div className="h-44 flex flex-col items-center justify-center gap-3 bg-zinc-50 px-3">
        <FileAudio className="h-8 w-8 text-zinc-400" />
        <audio src={fileUrl(a)} controls className="w-full h-8" />
      </div>
    );
  }
  if (mime.startsWith("video/")) {
    return <video src={fileUrl(a)} controls className="w-full h-44 object-cover bg-black" />;
  }
  if (mime.startsWith("text/")) {
    return (
      <div className="h-44 overflow-auto bg-zinc-50 p-3 text-xs text-zinc-700 whitespace-pre-wrap">
        {a.text_preview || (a.description || "").slice(0, 400) || "Text asset"}
      </div>
    );
  }
  return (
    <a href={fileUrl(a)} target="_blank" rel="noreferrer"
      className="h-44 grid place-items-center bg-zinc-50 text-zinc-400 hover:text-zinc-600">
      <FileText className="h-8 w-8" />
    </a>
  );
}

function KindBadge({ a }) {
  if (a.origin === "url") {
    const label = a.video_provider === "vimeo" ? "Vimeo" : "YouTube";
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-red-50 text-red-700 border-red-200 flex-shrink-0">
        <Youtube className="h-3 w-3 mr-1" /> {label}
      </Badge>
    );
  }
  if (a.origin === "ai") {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-purple-50 text-purple-700 border-purple-200 flex-shrink-0">
        <Bot className="h-3 w-3 mr-1" /> AI created
      </Badge>
    );
  }
  return (
    <Badge variant="outline"
      className="rounded-sm text-[10px] bg-sky-50 text-sky-700 border-sky-200 flex-shrink-0">
      <Upload className="h-3 w-3 mr-1" /> Uploaded
    </Badge>
  );
}

function KindIcon({ a }) {
  const mime = a.mime || "";
  if (a.origin === "url") return <Youtube className="h-3 w-3" />;
  if (a.kind === "text" || mime.startsWith("text/")) return <TypeIcon className="h-3 w-3" />;
  if (mime.startsWith("audio/")) return <FileAudio className="h-3 w-3" />;
  if (mime.startsWith("video/") || a.kind === "video") return <FileVideo className="h-3 w-3" />;
  return <FileText className="h-3 w-3" />;
}

/* ── Upload dialog ───────────────────────────────────────────── */
function UploadDialog({ open, onOpenChange, clientId, onUploaded }) {
  const [staged, setStaged] = useState([]); // {file, name, description, license}
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ i: 0, total: 0 });
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setStaged([]); setProgress({ i: 0, total: 0 }); }
  }, [open]);

  const stage = (fileList) => {
    const items = Array.from(fileList).map((f) => ({
      file: f,
      name: f.name.replace(/\.[^.]+$/, ""),
      description: "",
      license: "",
    }));
    setStaged((prev) => [...prev, ...items]);
  };
  const updateField = (i, k, v) =>
    setStaged((prev) => prev.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const removeAt = (i) => setStaged((prev) => prev.filter((_, idx) => idx !== i));

  const doUpload = async () => {
    if (staged.length === 0) return;
    setUploading(true);
    setProgress({ i: 0, total: staged.length });
    let ok = 0;
    for (let i = 0; i < staged.length; i += 1) {
      const s = staged[i];
      setProgress({ i: i + 1, total: staged.length });
      try {
        const fd = new FormData();
        fd.append("file", s.file);
        fd.append("name", s.name);
        fd.append("description", s.description);
        fd.append("license", s.license);
        await api.post(`/creative/clients/${clientId}/assets`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        toast.error(`${s.file.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    if (ok > 0) {
      toast.success(`Uploaded ${ok}/${staged.length} file(s) to the Library`);
      onUploaded && onUploaded();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="library-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload assets to Library</DialogTitle>
          <DialogDescription>
            Add images, audio, video, or documents. Optionally set a name, description
            and licence for each file — these appear in the Library card.
          </DialogDescription>
        </DialogHeader>

        <div
          className={`rounded-md border-2 border-dashed p-6 text-center text-sm transition-colors
            ${dragOver ? "border-sky-400 bg-sky-50" : "border-zinc-300 bg-zinc-50 hover:bg-zinc-100"}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files?.length) stage(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          data-testid="library-upload-dropzone">
          <Upload className="h-6 w-6 mx-auto mb-1 text-zinc-400" />
          <div className="text-zinc-700 font-medium">
            Drop files here or <span className="text-sky-700 underline">browse</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            Images, audio, video, PDFs, text — multiple files allowed.
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && stage(e.target.files)}
            data-testid="library-upload-input"
          />
        </div>

        {staged.length > 0 && (
          <div className="max-h-72 overflow-y-auto space-y-2 border rounded-md p-2 bg-white">
            {staged.map((s, i) => (
              <div key={i} className="border rounded-sm p-2 bg-zinc-50/60 space-y-1.5"
                data-testid={`library-upload-stage-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-mono text-zinc-500 truncate">{s.file.name}</div>
                    <div className="text-[10px] text-zinc-400">
                      {(s.file.size / 1024).toFixed(0)} KB · {s.file.type || "unknown"}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                    onClick={() => removeAt(i)} disabled={uploading}
                    data-testid={`library-upload-remove-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-1.5">
                  <Input value={s.name} placeholder="Name"
                    onChange={(e) => updateField(i, "name", e.target.value)}
                    className="h-8 text-xs" disabled={uploading}
                    data-testid={`library-upload-name-${i}`} />
                  <Input value={s.license} placeholder="Licence (optional)"
                    onChange={(e) => updateField(i, "license", e.target.value)}
                    className="h-8 text-xs" disabled={uploading}
                    data-testid={`library-upload-license-${i}`} />
                </div>
                <Textarea value={s.description} placeholder="Description (optional)"
                  onChange={(e) => updateField(i, "description", e.target.value)}
                  className="text-xs min-h-[36px]" rows={2} disabled={uploading}
                  data-testid={`library-upload-desc-${i}`} />
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {uploading && (
            <div className="text-xs text-zinc-500 mr-auto flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Uploading {progress.i}/{progress.total}…
            </div>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={doUpload} disabled={staged.length === 0 || uploading}
            data-testid="library-upload-submit-btn">
            {uploading
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Upload className="h-4 w-4 mr-1" />}
            Upload {staged.length > 0 && `(${staged.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── URL asset dialog ────────────────────────────────────────── */
function UrlDialog({ open, onOpenChange, clientId, onAdded }) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setUrl(""); setName(""); setDescription(""); }
  }, [open]);

  const save = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.post(`/creative/clients/${clientId}/url-assets`, {
        url: url.trim(),
        name: name.trim() || null,
        description: description.trim(),
      });
      toast.success("Video URL added to the Library");
      onAdded && onAdded();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add video URL");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="library-url-dialog">
        <DialogHeader>
          <DialogTitle>Add a hosted video URL</DialogTitle>
          <DialogDescription>
            YouTube or Vimeo link — we&apos;ll fetch the thumbnail, title and duration.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Video URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://youtu.be/…"
              data-testid="library-url-input" />
          </div>
          <div>
            <Label className="text-xs">Name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="library-url-name" />
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} data-testid="library-url-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !url.trim()}
            data-testid="library-url-submit-btn">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Add to Library
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CreativeLibraryPage() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("all"); // all | upload | ai | url
  const [uploadOpen, setUploadOpen] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  const reload = useCallback(() => {
    api.get(`/creative/clients/${clientId}/assets`)
      .then((r) => setAssets(r.data || [])).catch(() => setAssets([]));
  }, [clientId]);
  useEffect(() => { reload(); }, [reload]);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}" from the Library? This can't be undone.`)) return;
    try {
      await api.delete(`/creative/assets/${a.asset_id}`);
      toast.success("Asset deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const shown = assets.filter((a) => filter === "all" || a.origin === filter);

  return (
    <div className="space-y-5 max-w-6xl" data-testid="creative-library-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs label-overline text-[#002FA7]">
            <LibraryBig className="h-3 w-3" /> Library
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            Every creative asset for this client — uploads, AI creations, and
            hosted video URLs, with ownership and licence details.
          </p>
        </div>
        <div className="flex flex-wrap gap-1 items-center">
          {isAdmin && (
            <>
              <Button size="sm" className="rounded-sm h-8 text-xs bg-[#002FA7] text-white hover:bg-[#00227A]"
                onClick={() => setUploadOpen(true)}
                data-testid="library-upload-btn">
                <Plus className="h-3 w-3 mr-1" /> Upload files
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
                onClick={() => setUrlOpen(true)}
                data-testid="library-add-url-btn">
                <LinkIcon className="h-3 w-3 mr-1" /> Add video URL
              </Button>
              <span className="mx-1 h-5 w-px bg-zinc-200" />
            </>
          )}
          {[["all", "All"], ["upload", "Uploaded"], ["ai", "AI created"], ["url", "Hosted URL"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={filter === k ? "default" : "outline"}
              className={`rounded-sm h-8 text-xs ${filter === k ? "bg-[#002FA7] text-white hover:bg-[#00227A]" : ""}`}
              onClick={() => setFilter(k)} data-testid={`library-filter-${k}`}>
              {l}
            </Button>
          ))}
        </div>
      </header>

      {!shown.length ? (
        <Card className="rounded-sm">
          <CardContent className="p-10 text-center text-sm text-zinc-400" data-testid="library-empty">
            <LibraryBig className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No assets here yet — upload files{isAdmin ? <> using the <b>Upload files</b> button above</> : <></>} or create some on the <b>Creative</b> tab.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
          {shown.map((a) => (
            <Card key={a.asset_id} className="rounded-sm overflow-hidden"
              data-testid={`library-asset-${a.asset_id}`}>
              <Preview a={a} />
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate flex items-center gap-1"
                      title={a.name}>
                      <KindIcon a={a} /> {a.name}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-400 truncate">
                      {a.origin === "url"
                        ? `${a.video_provider} · ${a.video_id || ""}`
                        : `${a.filename || ""} ${fmtSize(a.size)}`}
                    </div>
                  </div>
                  <KindBadge a={a} />
                </div>

                {a.description && (
                  <p className="text-xs text-zinc-600 line-clamp-3">{a.description}</p>
                )}

                <div className="text-[11px] text-zinc-500">
                  <span className="font-semibold">Owner:</span> {a.author || "—"} · {fmtDate(a.created_at)}
                </div>

                {a.origin === "ai" && a.ai && (
                  <div className="text-[11px] text-zinc-500">
                    <span className="font-semibold">AI used:</span>{" "}
                    {a.ai.provider_label || a.ai.provider}
                    {a.ai.model ? ` (${a.ai.model})` : ""}
                  </div>
                )}

                {a.origin === "url" && (
                  <a href={a.url} target="_blank" rel="noreferrer"
                    className="text-[11px] text-sky-700 hover:underline inline-flex items-center gap-1"
                    data-testid={`library-url-link-${a.asset_id}`}>
                    <ExternalLink className="h-3 w-3" /> Open on {a.video_provider}
                  </a>
                )}

                {a.license && (
                  <div className="rounded-sm bg-zinc-50 border border-zinc-200 p-2 text-[10px] text-zinc-600 flex gap-1.5">
                    <Scale className="h-3 w-3 flex-shrink-0 mt-0.5 text-zinc-400" />
                    <span className="line-clamp-4" title={a.license}>{a.license}</span>
                  </div>
                )}

                {isAdmin && (
                  <div className="pt-1">
                    <Button size="sm" variant="ghost"
                      className="rounded-sm h-7 text-xs text-red-600 hover:text-red-700 px-2"
                      onClick={() => remove(a)}
                      data-testid={`library-delete-${a.asset_id}`}>
                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen}
        clientId={clientId} onUploaded={reload} />
      <UrlDialog open={urlOpen} onOpenChange={setUrlOpen}
        clientId={clientId} onAdded={reload} />
    </div>
  );
}
